import React from "react";
import { Link } from "react-router-dom";
import { setConsent, CONSENT_GRANTED } from "../../lib/consent";

/**
 * Footer — Phase 6 editorial coda.
 * Single centered column: wordmark, tagline, gold hairline, legal row, copyright.
 */
export const Footer = () => {
  const year = new Date().getFullYear();

  const reopenConsent = () => {
    // Clear stored decision so the CookieConsent banner re-mounts on next tick.
    try {
      window.localStorage.removeItem("quantum_consent_v1");
      // Reset by writing then removing triggers the effect in <CookieConsent /> via storage event on other tabs,
      // but for the current tab we force a soft reload.
      window.location.reload();
    } catch {
      // Fallback: simulate a granted event so a listener can react; then reload.
      setConsent(CONSENT_GRANTED);
      window.location.reload();
    }
  };

  const links = [
    { label: "Privacy Policy", to: "/legal/privacy", type: "link" },
    { label: "Terms of Use", to: "/legal/terms", type: "link" },
    { label: "Cookie Policy", to: "/legal/cookies", type: "link" },
    { label: "Legal Notice", to: "/legal/notice", type: "link" },
    { label: "Cookie Preferences", type: "button", onClick: reopenConsent },
    { label: "Contact", to: "/contact", type: "link" },
  ];

  return (
    <footer
      data-testid="site-footer"
      data-section-tone="dark"
      className="bg-[var(--q-bg-dark)] text-offwhite"
    >
      <div className="max-w-[1080px] mx-auto px-6 sm:px-10 lg:px-12 py-24 sm:py-28 lg:py-32 text-center">
        <p
          className="font-serif text-offwhite"
          style={{
            fontSize: "clamp(64px, 8vw, 128px)",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          QUANTUM
        </p>
        <p
          className="mt-10 sm:mt-12 font-serif italic text-offwhite/80"
          style={{ fontSize: "clamp(20px, 2.2vw, 32px)" }}
        >
          The Art of Capital.
        </p>

        <span
          aria-hidden="true"
          className="mt-20 sm:mt-24 block h-px w-12 bg-gold mx-auto"
        />

        <ul
          className="mt-10 sm:mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 font-sans uppercase text-offwhite/55"
          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
        >
          {links.map((l, i) => (
            <li key={l.label} className="inline-flex items-center gap-6">
              {l.type === "link" ? (
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group inline-flex hover:text-offwhite transition-colors"
                >
                  <span className="relative">
                    {l.label}
                    <span
                      aria-hidden="true"
                      className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-[250ms] ease-out"
                    />
                  </span>
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={l.onClick}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group inline-flex hover:text-offwhite transition-colors font-sans uppercase"
                  style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                >
                  <span className="relative">
                    {l.label}
                    <span
                      aria-hidden="true"
                      className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-[250ms] ease-out"
                    />
                  </span>
                </button>
              )}
              {i < links.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="inline-block bg-gold"
                  style={{ width: 4, height: 4 }}
                />
              ) : null}
            </li>
          ))}
        </ul>

        <p
          className="mt-10 sm:mt-12 font-sans text-offwhite/40"
          style={{ fontSize: "11px", letterSpacing: "0.02em" }}
        >
          © {year} Quantum US Capital Partners LLC
        </p>
      </div>
    </footer>
  );
};

export default Footer;
